import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { ArrowLeft, ExternalLink } from "lucide-react";
import Seo from "../components/Seo";
import { trackSpiritClick } from "../lib/analytics";

// Topics that benefit from a SPIRIT cross-link.
const SPIRIT_TOPICS = [
  "provenance",
  "genetic",
  "germplasm",
  "population diversity",
  "climate",
  "adaptation",
  "ecological intelligence",
  "cultural governance",
  "cultural intelligence",
  "restoration intelligence",
  "seed intelligence",
  "data governance",
  "gis",
  "spatial",
];

function isSpiritRelevant(item) {
  if (!item) return false;
  const haystack = [
    item.category,
    item.title,
    item.summary,
    ...(item.tags || []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return SPIRIT_TOPICS.some((kw) => haystack.includes(kw));
}

export default function ResourceDetail() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/resources/${id}`);
        setItem(data);
      } catch (e) {
        setErr("Not found");
      }
    })();
  }, [id]);

  if (err) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="resource-not-found">
        <p className="specimen-label mb-4">404</p>
        <h1 className="font-serif text-4xl mb-6">Resource not found.</h1>
        <Link to="/resources" className="link-mono text-eucalypt">Back to library</Link>
      </div>
    );
  }
  if (!item) {
    return <div className="max-w-3xl mx-auto px-6 py-32"><p className="specimen-label">Loading…</p></div>;
  }

  const spiritRelevant = isSpiritRelevant(item);
  const spiritSource = `resource:${item.slug || item.id}`;

  return (
    <article data-testid="page-resource-detail">
      <Seo
        title={item.title}
        description={item.summary}
        path={`/resources/${item.id}`}
        image={item.cover_image}
        type="article"
      />
      <section className="border-b border-rule">
        <div className="max-w-4xl mx-auto px-6 md:px-10 pt-20 pb-12">
          <Link to="/resources" className="link-mono text-eucalypt inline-flex items-center gap-2 mb-10" data-testid="back-to-resources">
            <ArrowLeft size={14} /> Back to Library
          </Link>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-3 mb-6">
            <p className="specimen-label">
              {item.category} · {new Date(item.created_at).toLocaleDateString("en-AU", { year: "numeric", month: "long", day: "numeric" })}
            </p>
            {spiritRelevant && (
              <a
                href="https://seedkeepers.app"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackSpiritClick(spiritSource, "header_badge")}
                data-testid="resource-spirit-badge"
                className="group inline-flex items-center gap-2 border border-ochre2 bg-cream text-ochre2 px-3 py-1.5 font-mono text-[10px] tracking-[0.22em] uppercase hover:bg-ochre2 hover:text-chalk transition-colors"
                aria-label="View related data on the SPIRIT platform"
                title="Related provenance, genetic or climate data lives in SPIRIT — for registered partners"
              >
                View on SPIRIT
                <ExternalLink size={11} className="opacity-70 group-hover:opacity-100" />
              </a>
            )}
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.02]">{item.title}</h1>
          <p className="mt-6 font-serif italic text-xl md:text-2xl text-eucalypt max-w-3xl">{item.summary}</p>
        </div>
      </section>

      {item.cover_image && (
        <div className="border-b border-rule">
          <div className="max-w-5xl mx-auto px-6 md:px-10 py-12">
            <img src={item.cover_image} alt={item.title} className="w-full h-[480px] object-cover border border-rule p-2 bg-cream" />
          </div>
        </div>
      )}

      <section>
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="prose-content font-serif text-lg md:text-xl leading-[1.7] text-ink/90 whitespace-pre-line">
            {item.body}
          </div>

          {spiritRelevant && (
            <aside
              data-testid="resource-spirit-callout"
              className="mt-14 border border-rule bg-parchment/60 p-2"
            >
              <div className="bg-bark text-chalk p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6">
                <div className="flex-1">
                  <p className="specimen-label text-ochre mb-3">
                    Related data lives in SPIRIT<sup className="text-[0.7em] align-super">TM</sup>
                  </p>
                  <p className="font-serif text-xl md:text-2xl leading-snug">
                    Provenance, genetic, ecological and climate datasets behind this story
                    are managed in SPIRIT — available to registered SeedKeepers partners.
                  </p>
                </div>
                <a
                  href="https://seedkeepers.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackSpiritClick(spiritSource, "inline_callout")}
                  data-testid="resource-spirit-callout-cta"
                  className="group inline-flex items-center justify-center gap-3 bg-ochre text-bark px-6 py-4 font-mono text-[11px] tracking-[0.28em] uppercase hover:bg-chalk rounded-full hover:-translate-y-0.5 transition-all whitespace-nowrap flex-shrink-0"
                >
                  Access SPIRIT{" "}
                  <ExternalLink
                    size={14}
                    className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                  />
                </a>
              </div>
            </aside>
          )}

          <div className="rule mt-16 pt-6 flex flex-wrap gap-2">
            {(item.tags || []).map((t) => (
              <span key={t} className="inline-block border border-rule px-3 py-1 text-[10px] tracking-widest uppercase text-muted font-mono">
                {t}
              </span>
            ))}
          </div>
          <p className="mt-10 specimen-label">Filed by {item.author || "SeedKeepers"}</p>
        </div>
      </section>
    </article>
  );
}
